import { Component, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { MenuIcon } from '@/components/MenuIcon';
import { ApiRequestError } from '@/lib/api/client';
import { safeHref } from '@/lib/utils/url';

/**
 * 위젯 한 칸의 껍데기.
 *
 * 홈의 위젯은 <b>서로를 기다리지 않는다</b>. 위젯마다 자기 쿼리를 갖고, 자기 자리에서 스피너를
 * 돌리다 자기 자리만 채운다. 하나를 위해 홈 전체가 비어 있는 순간을 만들지 않기 위해서다 —
 * 열 개 중 하나가 느리면 열 개가 다 늦게 뜨는 화면이 되고, 그건 느린 화면으로 기억된다.
 *
 * 그래서 로딩·오류·빈 목록 세 상태를 여기서 처리한다. 위젯 코드는 "무엇을 그릴지"만 적는다.
 * 높이를 고정해 두는 것도 같은 이유다. 늦게 도착한 위젯이 자리를 넓히면 이미 읽고 있던
 * 다른 위젯이 아래로 밀려난다.
 */

// 메이슨리로 바뀌면서 위젯이 자기 높이대로 놓인다. 이 값은 "짧아도 이만큼은" 하는 바닥일 뿐이라
// 낮게 둔다 — 높게 두면 짧은 위젯까지 늘어나 다시 다 비슷한 키가 된다(메이슨리가 무의미해진다).
const BODY_MIN_HEIGHT = 96;

export function WidgetFrame({
  title,
  icon,
  to,
  toLabel = '전체보기',
  toTourKey,
  titleBadge,
  headerRight,
  pending = false,
  error = null,
  empty = false,
  emptyText = '표시할 내용이 없습니다.',
  onRetry,
  children,
}: {
  title: string;
  icon: string;
  /** 자세히 볼 화면. 권한이 없어 못 가는 위젯이면 비워 둔다. */
  to?: string;
  toLabel?: string;
  /**
   * 안내가 이 링크를 <b>따로 짚을</b> 때 쓰는 이름(`data-tour`).
   *
   * <p>위젯 칸 전체는 {@code WidgetMasonry} 가 이미 이름을 달아 준다. 이 값은 그 안에서
   * 「전체보기를 누르세요」 를 반짝여야 하는 위젯만 준다 — 사이드바에서 메뉴가 내려가 <b>이 링크가
   * 유일한 입구</b>가 된 자리(뉴스·건설동향)가 그렇다. 위젯마다 이름이 달라야 하므로 여기서
   * 기본값을 만들지 않는다: 같은 이름이 여럿이면 안내가 화면의 첫 번째를 짚는다.
   */
  toTourKey?: string;
  /**
   * 제목 <b>바로 옆</b>에 붙는 것 (사용자 지시 2026-09-30 — 「숫자를 TODO 할일 글씨 옆으로」).
   *
   * <p>{@link headerRight} 와 자리가 다르다. 저쪽은 오른쪽 끝, 전체보기 링크와 <b>같은 묶음</b>이라
   * 배지를 두면 「⑦ 전체보기 ›」 로 붙어 선다 — 숫자가 링크의 일부처럼 읽힌다. 이 칸은 제목의
   * 일부다: 「TO DO 할일 ⑦」 은 «무엇이 몇 건인가» 한 문장으로 읽히고, 오른쪽 끝은 비워 둔다.
   */
  titleBadge?: ReactNode;
  headerRight?: ReactNode;
  pending?: boolean;
  error?: Error | null;
  empty?: boolean;
  /**
   * 비어 있을 때의 문구. <b>위젯마다 자기 말로 적는다</b> — "표시할 내용이 없습니다" 는
   * 무엇이 없는지 답하지 않아 사용자가 고장으로 읽는다. 기본값은 넘기는 것을 잊었을 때의
   * 마지막 안전망일 뿐이다(예: "오늘 예정된 회의 일정이 없습니다").
   */
  emptyText?: string;
  /**
   * '다시 시도' 버튼이 부를 것(보통 react-query 의 `refetch`).
   *
   * <b>외부에서 받아오는 위젯만 준다</b> — 날씨·뉴스·건설동향. 기상 API 나 수집기가 잠깐 못
   * 답하는 일은 실제로 있고, 다시 누르면 살아난다. 반대로 할일·오늘 회의처럼 우리 DB 를 읽는
   * 위젯은 비어 있는 것이 정상이지 실패가 아니다. 그 자리에 다시 시도를 놓으면 "없다" 를
   * "고장 났다" 로 읽게 만든다.
   */
  onRetry?: () => void;
  children?: ReactNode;
}) {
  const failure = error ? describeError(error, emptyText) : null;

  return (
    <section className="card p-4 flex flex-col">
      <div className="flex items-center gap-2 mb-3">
        <span
          className="flex items-center justify-center flex-shrink-0"
          style={{ width: '28px', height: '28px', borderRadius: '8px', background: 'var(--accent-bg)', color: 'var(--accent)' }}
        >
          <MenuIcon name={icon} size={15} />
        </span>
        {/* 제목은 본문(ui-md)보다 한 단만 크다. 예전 text-sm(14px)은 위젯이 3열로 갈린 화면에서
            제목 줄 혼자 커 보였다. */}
        <h2 className="text-ui-lg font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
          {title}
        </h2>
        {titleBadge && <span className="flex-shrink-0">{titleBadge}</span>}
        <div className="ml-auto flex items-center gap-2 flex-shrink-0">
          {headerRight}
          {to && (
            <Link to={to} data-tour={toTourKey} className="text-ui-xs" style={{ color: 'var(--text-muted)' }}>
              {toLabel} ›
            </Link>
          )}
        </div>
      </div>

      <div className="flex-1" style={{ minHeight: `${BODY_MIN_HEIGHT}px` }}>
        {pending ? (
          <WidgetSkeleton />
        ) : failure ? (
          <WidgetMessage
            hint={failure.hint}
            action={failure.retryable && onRetry ? <RetryButton onClick={onRetry} /> : undefined}
          >
            {failure.text}
          </WidgetMessage>
        ) : empty ? (
          <WidgetMessage>{emptyText}</WidgetMessage>
        ) : (
          children
        )}
      </div>
    </section>
  );
}

/** 내용이 도착할 때까지의 자리. 실제 줄 수와 비슷해야 자리가 덜 흔들린다. */
function WidgetSkeleton() {
  return (
    <div className="space-y-2.5 pt-1" aria-hidden>
      {[80, 100, 65, 90].map((width, index) => (
        <div
          key={index}
          style={{ width: `${width}%`, height: '12px', borderRadius: '6px', background: 'var(--bg-hover)' }}
        />
      ))}
    </div>
  );
}

/**
 * 실패를 사람 말로 바꾼다.
 *
 * 모든 실패가 사고는 아니다. <b>볼 자료가 없거나</b>(404) <b>볼 권한이 없거나</b>(403)
 * <b>기능이 아직 켜지지 않은</b>(503) 경우는 사용자가 할 수 있는 일이 없고, 잘못된 것도 없다.
 * 그런 상태에 빨간 글씨로 "불러오지 못했습니다" 를 띄우면 멀쩡한 홈이 고장 난 것처럼 보인다.
 * 그래서 이 셋은 <b>빈 목록과 같은 톤</b>으로 말하고, 다시 시도 버튼도 주지 않는다 —
 * 눌러도 같은 답이 온다.
 *
 * 나머지(네트워크 끊김·5xx)만 실제 실패다. 그때도 색으로 겁을 주는 대신 무엇을 하면 되는지
 * (다시 시도) 를 준다. 위젯 한 칸이 비는 일은 홈 전체가 멈추는 일이 아니다.
 */
function describeError(error: Error, emptyText: string): { text: string; retryable: boolean; hint?: string } {
  const hint = devHint(error);

  if (error instanceof ApiRequestError) {
    if (error.status === 404) return { text: emptyText, retryable: false, hint };
    if (error.status === 403) return { text: '볼 수 있는 권한이 없습니다.', retryable: false, hint };
    if (error.status === 401) return { text: '로그인이 만료되었습니다. 다시 로그인해 주세요.', retryable: false, hint };
    // 503 은 "아직 준비되지 않았다" 는 뜻이다(외부 연동 미설정·일시 장애). 서버가 사람이 읽을
    // 문장을 함께 주므로 그대로 쓴다 — 위젯마다 다른 이유를 여기서 다시 추측하지 않는다.
    // 잠시 뒤 살아나는 쪽이 많아 다시 시도는 남겨 둔다.
    if (error.status === 503) return { text: error.apiError?.message ?? '아직 준비 중입니다.', retryable: true, hint };
  }
  return { text: '내용을 가져오는 중 입니다.', retryable: true, hint };
}

/**
 * 개발할 때만 붙는 한 줄 — <b>어느 위젯이 왜 실패했는지</b>를 화면에서 바로 읽게 한다.
 *
 * 사용자에게는 상태 코드가 아무 의미도 없어서 문구를 뭉갰는데, 그러고 나면 개발자도 화면만 보고는
 * 401 인지 500 인지 알 수 없다. 개발자도구를 열어 네트워크 탭을 뒤지는 대신 여기서 답한다.
 * 운영 번들에는 `import.meta.env.DEV` 가 false 라 이 문자열 자체가 남지 않는다.
 */
function devHint(error: Error): string | undefined {
  if (!import.meta.env.DEV) return undefined;
  if (error instanceof ApiRequestError) {
    return `HTTP ${error.status}${error.apiError?.code ? ` · ${error.apiError.code}` : ''}`;
  }
  return error.message || error.name;
}

function WidgetMessage({ hint, action, children }: { hint?: string; action?: ReactNode; children: ReactNode }) {
  return (
    <div
      className="flex flex-col items-center justify-center gap-2 h-full text-center px-2 text-ui-sm"
      style={{ color: 'var(--text-muted)', wordBreak: 'keep-all' }}
    >
      <span>{children}</span>
      {hint && <code className="text-ui-2xs" style={{ color: 'var(--text-muted)', opacity: 0.7 }}>{hint}</code>}
      {action}
    </div>
  );
}

function RetryButton({ onClick }: { onClick: () => void }) {
  return (
    <button onClick={onClick} className="btn-secondary btn-xs">
      다시 시도
    </button>
  );
}

/**
 * 위젯 하나가 렌더 중 예외를 던져도 홈 전체가 하얗게 되지 않도록 막는다.
 *
 * 쿼리 실패는 `WidgetFrame` 의 `error` 가 받지만, 응답 모양이 예상과 달라 그리는 도중
 * 터지는 경우는 잡지 못한다. 그때 홈 전체가 사라지면 사용자는 다른 화면으로도 못 간다 —
 * 사이드바까지 함께 날아가기 때문이다.
 */
export class WidgetBoundary extends Component<{ title: string; children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error) {
    // 오류 신고 파이프라인이 붙기 전까지는 콘솔에 남긴다. 조용히 삼키면 원인을 못 찾는다.
    console.error(`[home widget] ${this.props.title}`, error);
  }

  render() {
    if (this.state.failed) {
      return (
        <WidgetFrame
          title={this.props.title}
          icon="grid"
          error={new Error('render failed')}
          // 다시 그려 보게 한다. 같은 응답이면 또 실패하지만, 응답이 바뀌었으면 이걸로 살아난다.
          onRetry={() => this.setState({ failed: false })}
        />
      );
    }
    return this.props.children;
  }
}

/**
 * 위젯 안에서 반복되는 목록 한 줄.
 *
 * `to` 는 사내 화면(라우터), `href` 는 <b>사외 링크</b>다. 둘을 나눈 이유는 사외 링크에
 * `target="_blank"` 와 `rel="noopener noreferrer"` 가 반드시 붙어야 하기 때문이다 —
 * 줄마다 그것을 다시 적게 하면 언젠가 한 곳이 빠진다. 둘 다 주면 `to` 가 이긴다.
 */
export function WidgetRow({
  to,
  href,
  leading,
  title,
  meta,
  trailing,
}: {
  to?: string;
  href?: string;
  leading?: ReactNode;
  title: ReactNode;
  meta?: ReactNode;
  trailing?: ReactNode;
}) {
  const body = (
    <>
      {leading}
      <span className="min-w-0 flex-1 truncate text-ui-md" style={{ color: 'var(--text-secondary)' }}>
        {title}
      </span>
      {meta && (
        <span className="flex-shrink-0 text-ui-xs" style={{ color: 'var(--text-muted)' }}>
          {meta}
        </span>
      )}
      {trailing}
    </>
  );

  const className = 'flex items-center gap-2 py-1.5 rounded-md';

  if (to) {
    return (
      <Link to={to} className={`${className} hover:bg-slate-50`}>
        {body}
      </Link>
    );
  }

  if (href) {
    return (
      <a href={safeHref(href)} target="_blank" rel="noopener noreferrer" className={`${className} hover:bg-slate-50`}>
        {body}
      </a>
    );
  }

  return <div className={className}>{body}</div>;
}
